import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    // Show loading state while checking authentication
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to login if not authenticated
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Company-less users split into two journeys: fresh social signups finish
  // onboarding; users whose memberships were revoked get the existing
  // contact-your-administrator page. The revoked guard is NOT exempt for
  // /complete-signup — a revoked user must not reach onboarding (the API
  // and page block them too; this keeps all three layers in agreement).
  if (user?.needsCompanySetup && location.pathname !== '/complete-signup') {
    return <Navigate to="/complete-signup" replace />;
  }
  if (
    user &&
    !user.company &&
    user.hasPriorMembership &&
    location.pathname !== '/no-active-company'
  ) {
    return <Navigate to="/no-active-company" replace />;
  }

  return <>{children}</>;
};
